import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Optional footer slot — typically Cancel / Save buttons. */
  footer?: ReactNode;
  /**
   * Optional controls rendered in the header to the left of the close (×)
   * button — e.g. edit / delete icon actions.
   */
  headerActions?: ReactNode;
  /** Inhibit closing while a mutation is in flight. */
  busy?: boolean;
  /** 'lg' widens + fixes the panel height for large content (e.g. a document viewer). */
  size?: 'md' | 'lg';
  /** Strip the body's padding + gap so a child (iframe/img) can fill edge-to-edge. */
  bodyFlush?: boolean;
}

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
  headerActions,
  busy = false,
  size = 'md',
  bodyFlush = false,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);

  // ESC closes (unless busy). Scroll-lock the body while open so the
  // page underneath doesn't bounce when the user scrolls inside the
  // modal on a long form.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, busy]);

  // Move focus into the panel when it opens so screen readers and
  // keyboard navigation pick it up.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  // Portalled to <body>, and submit stopped at the overlay. A modal opened from
  // inside a form (VendorPicker's "+ Add new vendor…" on New Expense) used to
  // render INSIDE that form, so its own <form> was nested in the page's: "Add
  // Vendor" submitted the EXPENSE instead — the vendor was never created, and
  // the expense saved or navigated away, losing what had been typed. The
  // portal fixes the DOM nesting (each button's form owner is its own form
  // again); the stopPropagation fixes the rest, because React still bubbles a
  // portalled event through the React tree to the page form's onSubmit.
  return createPortal(
    <div
      className="modal-overlay"
      role="presentation"
      onSubmit={(e) => e.stopPropagation()}
      onMouseDown={(e) => {
        // Click-outside close — only when the press lands on the overlay
        // itself (not a drag that started inside the panel).
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={size === 'lg' ? 'modal-panel modal-panel--lg' : 'modal-panel'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header className="modal-header">
          <h2>{title}</h2>
          <div className="modal-header-actions">
            {headerActions}
            <button
              type="button"
              className="modal-close"
              onClick={onClose}
              disabled={busy}
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </header>
        <div className={bodyFlush ? 'modal-body modal-body--flush' : 'modal-body'}>{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
